import { useEffect, useId, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { apiService, type Classroom } from "@/services/api.service";
import type { FlagScope } from "@/services/flag-store.service";

type Option = { id: string; label: string; detail?: string };
const MAX_OPTIONS = 8;
const USER_SEARCH_DELAY_MS = 250;

const PLACEHOLDERS: Record<Exclude<FlagScope, "global">, string> = {
  user: "Search name, username, email or ID",
  classroom: "Search classrooms",
  org: "Search organizations",
};

export function TargetSearch({ scope, organizations, classrooms, selectedLabel, onSelect }: {
  scope: Exclude<FlagScope, "global">;
  organizations: { id: string; name: string }[];
  classrooms: Classroom[];
  selectedLabel: string;
  onSelect: (id: string, label: string) => void;
}) {
  const listId = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [userOptions, setUserOptions] = useState<Option[]>([]);
  const [searching, setSearching] = useState(false);
  const [active, setActive] = useState(0);

  const localOptions = useMemo<Option[]>(() => {
    const term = query.trim().toLowerCase();
    if (scope === "org") {
      return organizations
        .filter(org => !term || org.name.toLowerCase().includes(term) || org.id.toLowerCase().includes(term))
        .map(org => ({ id: org.id, label: org.name, detail: org.id }));
    }
    if (scope === "classroom") {
      return classrooms
        .filter(room => !term || room.name.toLowerCase().includes(term) || String(room.id) === term)
        .map(room => ({ id: String(room.id), label: room.name, detail: `#${room.id}` }));
    }
    return [];
  }, [scope, query, organizations, classrooms]);

  useEffect(() => {
    if (scope !== "user" || !query.trim()) return;
    let current = true;
    const timer = setTimeout(() => {
      setSearching(true);
      apiService.getFeatureFlagsUsers({ query: query.trim(), limit: MAX_OPTIONS, page: 1, sort_field: "name", sort_order: "asc" })
        .then(result => {
          if (!current) return;
          setUserOptions(result.users.map(user => ({
            id: String(user.id),
            label: user.name || user.username || user.email,
            detail: `#${user.id} · ${user.email} · ${user.role.toLowerCase()}`,
          })));
        })
        .catch(() => { if (current) setUserOptions([]); })
        .finally(() => { if (current) setSearching(false); });
    }, USER_SEARCH_DELAY_MS);
    return () => { current = false; clearTimeout(timer); };
  }, [scope, query]);

  const options = (scope === "user" ? (query.trim() ? userOptions : []) : localOptions).slice(0, MAX_OPTIONS);

  function choose(option: Option) {
    onSelect(option.id, option.label);
    setQuery("");
    setOpen(false);
  }

  return <div className="relative w-72">
    <Input
      role="combobox"
      aria-label={PLACEHOLDERS[scope]}
      aria-expanded={open && options.length > 0}
      aria-controls={listId}
      aria-autocomplete="list"
      placeholder={selectedLabel || PLACEHOLDERS[scope]}
      className={selectedLabel ? "placeholder:text-foreground" : ""}
      value={query}
      onFocus={() => setOpen(true)}
      onBlur={() => setTimeout(() => setOpen(false), 150)}
      onChange={event => { setQuery(event.target.value); setActive(0); setOpen(true); }}
      onKeyDown={event => {
        if (event.key === "ArrowDown") { event.preventDefault(); setActive(index => Math.min(index + 1, options.length - 1)); }
        else if (event.key === "ArrowUp") { event.preventDefault(); setActive(index => Math.max(index - 1, 0)); }
        else if (event.key === "Enter" && options[active]) { event.preventDefault(); choose(options[active]); }
        else if (event.key === "Escape") setOpen(false);
      }}
    />
    {open && (options.length > 0 || searching || (scope === "user" && query.trim())) && <ul id={listId} role="listbox"
      className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border bg-popover p-1 text-sm shadow-md">
      {options.map((option, index) => <li key={option.id} role="option" aria-selected={index === active}
        onMouseDown={event => { event.preventDefault(); choose(option); }}
        onMouseEnter={() => setActive(index)}
        className={`cursor-pointer rounded px-2 py-1.5 ${index === active ? "bg-accent" : ""}`}>
        <span className="block font-medium">{option.label}</span>
        {option.detail && <span className="block text-xs text-muted-foreground">{option.detail}</span>}
      </li>)}
      {searching && <li className="px-2 py-1.5 text-muted-foreground" role="status">Searching…</li>}
      {!searching && options.length === 0 && <li className="px-2 py-1.5 text-muted-foreground">No matches</li>}
    </ul>}
  </div>;
}
